/**
 * Pagos — el libro de caja.
 *
 * LA REGLA QUE SEPARA ESTA PANTALLA DE CAJA: si tiene botón, es Caja; si solo
 * se lee, es Pagos.
 *
 * Y no listan lo mismo, que es lo que más confundía. Caja lista CITAS que
 * deben dinero —una cita "por cobrar" es justamente una que NO tiene pago
 * todavía, así que no puede salir en una lista de pagos: no existe la fila—.
 * Esta lista PAGOS, dinero que ya se registró. Caja mira hacia adelante, esto
 * hacia atrás.
 *
 * Aquí había hasta hace poco botones de aprobar y rechazar, y Caja mandaba a
 * esta pantalla para usarlos. Eso partía una misma tarea en dos sitios y hacía
 * que las dos pantallas parecieran la misma: la cajera miraba la constancia en
 * una y aprobaba en otra, con el paciente esperando. Validar es una acción y
 * se quedó donde está la persona, en Caja. Aquí "Por validar" es solo un
 * estado más por el que filtrar.
 *
 * POR QUÉ LOS FILTROS SON ASÍ
 *
 * Antes las pestañas eran "Por validar / De hoy / Todos", que mezcla dos
 * preguntas distintas —en qué estado está y de cuándo es— en un solo control.
 * Con eso no se podía contestar nada de lo que se le pregunta a una
 * contabilidad: cuánto entró del 1 al 15, qué pagó tal paciente, cuánto se
 * cobró por Yape este mes. Ahora el estado son pestañas (iguales a las de
 * Caja), la fecha es un rango con atajos, y hay un buscador que filtra según se
 * escribe.
 *
 * Todo el filtrado es en memoria y a propósito: la lista completa ya viene en
 * una sola petición, y pedirle al servidor cada tecla haría lenta una búsqueda
 * que así es instantánea. El día que esto pase de unos miles de filas, el
 * filtro se muda al backend y la pantalla no cambia.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, FileText, Search, Wallet, X } from "lucide-react";
import { type EstadoPago, type Pago, listPagos } from "../api/pagos";
import { useAuth } from "../auth/AuthProvider";
import { useBase, useZonaHoraria } from "../auth/TenantBrandingProvider";
import { diaEnZona, fechaHora, hoyEnZona } from "../lib/fecha";
import { ConstanciaViewer } from "../components/ConstanciaViewer";
import { Badge, Card, CardBody, CardHeader, Input } from "../components/ui";
import { EtiquetaOrigen } from "../components/EtiquetaOrigen";

/**
 * Cómo se escribe cada método.
 *
 * Aquí SÍ se quedan transferencia y tarjeta, aunque ya no se ofrezcan al
 * cobrar: esto es el libro, y si alguna vez hubo un pago con una de ellas hay
 * que saber leerlo. Dejar de nombrarlas haría que un cobro viejo apareciera
 * con su código en mayúsculas.
 */
const METODO_LABEL: Record<string, string> = {
  EFECTIVO: "Efectivo",
  YAPE: "Yape",
  PLIN: "Plin",
  TRANSFERENCIA: "Transferencia",
  TARJETA: "Tarjeta",
};

const ESTADO_UI: Record<EstadoPago, { label: string; variant: "success" | "warning" | "danger" }> = {
  PAGADO: { label: "Cobrado", variant: "success" },
  PENDIENTE_VALIDACION: { label: "Por validar", variant: "warning" },
  RECHAZADO: { label: "Rechazado", variant: "danger" },
};

type Pestana = "por_validar" | "cobrados" | "rechazados" | "todos";

const ESTADO_DE_PESTANA: Record<Pestana, EstadoPago | null> = {
  por_validar: "PENDIENTE_VALIDACION",
  cobrados: "PAGADO",
  rechazados: "RECHAZADO",
  todos: null,
};

/** Resta días a un YYYY-MM-DD sin salirse del calendario. */
function menosDias(dia: string, dias: number): string {
  const d = new Date(`${dia}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - dias);
  return d.toISOString().slice(0, 10);
}

export function PagosPage() {
  const { me } = useAuth();
  const tz = useZonaHoraria();
  const base = useBase();
  const hoy = hoyEnZona(tz);

  // Arranca en "Todos" porque esto es un libro de caja, no una bandeja: quien
  // entra viene a mirar el histórico. Lo pendiente se trabaja en Caja.
  const [pestana, setPestana] = useState<Pestana>("todos");
  const [busqueda, setBusqueda] = useState("");
  const [metodo, setMetodo] = useState<string>("");
  // Vacío = sin límite. Arrancar con un rango puesto escondería constancias
  // viejas sin validar, que es justo lo que no puede pasar desapercibido.
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [viendoConstancia, setViendoConstancia] = useState<Pago | null>(null);

  const puedeValidar = me?.is_super_admin || (me?.permisos.includes("pago:validar") ?? false);

  const { data: pagos = [], isLoading } = useQuery({
    queryKey: ["pagos"],
    queryFn: () => listPagos(),
  });

  // Fecha, método y texto se aplican ANTES que la pestaña: así los contadores
  // de cada pestaña dicen cuántos hay dentro de lo que el usuario está mirando
  // y no del total histórico, que es lo que hace que un "3" no cuadre con las
  // tres filas que se ven.
  const enRango = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return pagos.filter((p) => {
      // Comparar el día en la zona del policlínico, no el ISO en UTC: a las
      // 20:00 de Lima el created_at ya dice "mañana" en UTC.
      const dia = diaEnZona(p.created_at, tz);
      if (desde && dia < desde) return false;
      if (hasta && dia > hasta) return false;
      if (metodo && p.metodo !== metodo) return false;
      if (!texto) return true;
      const heno = [
        p.paciente_nombre,
        p.profesional_nombre,
        p.especialidad_nombre,
        METODO_LABEL[p.metodo] ?? p.metodo,
        p.nota,
        String(p.monto),
        `#${p.cita_id}`,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      // Cada palabra por separado: "dove yape" encuentra lo mismo escrito en
      // cualquier orden, que es como la gente busca de verdad.
      return texto.split(/\s+/).every((t) => heno.includes(t));
    });
  }, [pagos, busqueda, metodo, desde, hasta, tz]);

  const visibles = useMemo(() => {
    const estado = ESTADO_DE_PESTANA[pestana];
    return estado ? enRango.filter((p) => p.estado === estado) : enRango;
  }, [enRango, pestana]);

  const cuenta = (e: EstadoPago) => enRango.filter((p) => p.estado === e).length;

  // Los totales miran el RANGO, no la pestaña.
  //
  // Antes miraban la pestaña activa y el resultado era absurdo: estando en
  // "Por validar" las tarjetas decían S/ 0.00 y "0 pagos listados" mientras
  // justo debajo la pestaña de al lado decía 31 cobrados. Cuánto dinero entró
  // en unas fechas no cambia porque estés mirando una situación u otra.
  //
  // El total solo cuenta lo efectivamente cobrado: sumar lo pendiente daría un
  // cierre de caja que no cuadra con el dinero real.
  const cobrado = enRango
    .filter((p) => p.estado === "PAGADO")
    .reduce((s, p) => s + Number(p.monto), 0);

  const porMetodo = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of enRango) {
      if (p.estado !== "PAGADO") continue;
      m.set(p.metodo, (m.get(p.metodo) ?? 0) + Number(p.monto));
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [enRango]);

  const pendientesTotales = pagos.filter((p) => p.estado === "PENDIENTE_VALIDACION").length;
  const filtrando = Boolean(busqueda.trim() || metodo || desde || hasta);

  const rango = (d: string, h: string) => {
    setDesde(d);
    setHasta(h);
  };
  const atajoActivo = (d: string, h: string) => desde === d && hasta === h;

  const limpiar = () => {
    setBusqueda("");
    setMetodo("");
    setDesde("");
    setHasta("");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Pagos</h1>
        <p className="mt-1 text-sm text-slate-500">
          El histórico de lo cobrado: busca, filtra por fechas y cuadra totales.
          Cobrar y validar constancias se hace en Caja.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Resumen
          icono={<Wallet className="h-5 w-5" />}
          etiqueta={filtrando ? "Cobrado en lo filtrado" : "Cobrado en total"}
          valor={`S/ ${cobrado.toFixed(2)}`}
          color="bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-[color:var(--color-link)]"
        />
        <Resumen
          icono={<FileText className="h-5 w-5" />}
          etiqueta="Constancias por validar"
          valor={String(pendientesTotales)}
          // Este número ignora los filtros a propósito: es trabajo pendiente,
          // y una constancia sin revisar no deja de existir porque estés
          // mirando otro mes. Y como el trabajo se hace en Caja, desde aquí
          // solo se señala el camino.
          nota={
            pendientesTotales && puedeValidar ? (
              <Link to={`${base}/caja?ver=validar`} className="hover:underline">
                En todo el historial · validar en Caja →
              </Link>
            ) : pendientesTotales ? (
              "En todo el historial"
            ) : undefined
          }
          color={pendientesTotales ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}
        />
        <Resumen
          icono={<Check className="h-5 w-5" />}
          etiqueta="Pagos en el rango"
          valor={String(enRango.length)}
          nota={filtrando ? `de ${pagos.length} en total` : undefined}
          color="bg-slate-100 text-slate-600"
        />
      </div>

      {/* ---- Buscador y rango ---- */}
      <Card>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por paciente, profesional, especialidad o método…"
                className="pl-9"
                aria-label="Buscar pagos"
              />
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-500">Método</span>
              <select
                value={metodo}
                onChange={(e) => setMetodo(e.target.value)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                <option value="">Todos</option>
                {Object.entries(METODO_LABEL).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-500">Desde</span>
              <Input
                type="date"
                value={desde}
                max={hasta || undefined}
                onChange={(e) => setDesde(e.target.value)}
                className="w-auto"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-500">Hasta</span>
              <Input
                type="date"
                value={hasta}
                min={desde || undefined}
                onChange={(e) => setHasta(e.target.value)}
                className="w-auto"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-400">Atajos:</span>
            {([
              ["Hoy", hoy, hoy],
              ["Últimos 7 días", menosDias(hoy, 6), hoy],
              ["Este mes", `${hoy.slice(0, 7)}-01`, hoy],
              ["Todo", "", ""],
            ] as [string, string, string][]).map(([label, d, h]) => (
              <button
                key={label}
                type="button"
                onClick={() => rango(d, h)}
                aria-pressed={atajoActivo(d, h)}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
                  atajoActivo(d, h)
                    ? "border-slate-400 bg-slate-100 text-slate-800"
                    : "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-800"
                }`}
              >
                {label}
              </button>
            ))}
            {filtrando && (
              <button
                type="button"
                onClick={limpiar}
                className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800"
              >
                <X className="h-3.5 w-3.5" />
                Quitar filtros
              </button>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          {/* Las mismas pestañas que Caja, no unas píldoras azules: cambiar de
              lista dentro de una pantalla es el mismo gesto en los dos sitios y
              no puede tener dos aspectos distintos.

              Y ahora solo hablan de ESTADO. "De hoy" era una pestaña más, lo
              que obligaba a elegir entre filtrar por fecha o por situación; la
              fecha vive arriba, donde se combina con todo lo demás. */}
          <div className="flex flex-wrap gap-1 border-b border-slate-200">
            {([
              ["por_validar", "Por validar", cuenta("PENDIENTE_VALIDACION"), true],
              ["cobrados", "Cobrados", cuenta("PAGADO"), false],
              ["rechazados", "Rechazados", cuenta("RECHAZADO"), false],
              ["todos", "Todos", enRango.length, false],
            ] as [Pestana, string, number, boolean][]).map(([id, label, total, avisa]) => {
              const activa = pestana === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPestana(id)}
                  aria-current={activa ? "page" : undefined}
                  className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition ${
                    activa
                      ? "border-[color:var(--color-link)] text-[color:var(--color-link)]"
                      : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {label}
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                      avisa && total > 0
                        ? "bg-red-600 text-white"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {total}
                  </span>
                </button>
              );
            })}
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {isLoading && (
            <div className="px-6 py-12 text-center text-sm text-slate-400">Cargando…</div>
          )}
          {!isLoading && visibles.length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-slate-500">
              {filtrando
                ? "Ningún pago cuadra con lo que buscas."
                : pestana === "por_validar"
                  ? "No hay constancias pendientes. Todo al día."
                  : "Sin pagos que mostrar."}
            </div>
          )}

          {/* Sin esto, la pestaña "Por validar" es una lista de trabajo
              pendiente sin un solo botón: se ve el problema y no se ve la
              salida. Decir dónde está el botón cuesta una línea. */}
          {pestana === "por_validar" && visibles.length > 0 && puedeValidar && (
            <div className="flex flex-wrap items-center gap-2 border-b border-amber-100 bg-amber-50 px-6 py-3 text-sm text-amber-800">
              <span>Estas constancias se aprueban o se rechazan en Caja, con el paciente delante.</span>
              <Link
                to={`${base}/caja?ver=validar`}
                className="font-semibold underline underline-offset-2"
              >
                Ir a validarlas →
              </Link>
            </div>
          )}

          {visibles.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-y border-slate-200 bg-slate-50">
                  <tr>
                    <Th>Fecha</Th>
                    <Th>Paciente</Th>
                    <Th>Especialidad</Th>
                    <Th>Profesional</Th>
                    <Th>Método</Th>
                    <Th>Constancia</Th>
                    <Th>Estado</Th>
                    <Th right>Monto</Th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((p) => {
                    const ui = ESTADO_UI[p.estado];
                    return (
                      <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="whitespace-nowrap px-6 py-3 text-slate-600">
                          {fechaHora(p.created_at, tz)}
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">
                              {p.paciente_nombre ?? `Cita #${p.cita_id}`}
                            </span>
                            <EtiquetaOrigen origen={p.origen} />
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          {p.especialidad_nombre ? <Badge>{p.especialidad_nombre}</Badge> : "—"}
                        </td>
                        <td className="px-6 py-3 text-slate-600">{p.profesional_nombre ?? "—"}</td>
                        <td className="whitespace-nowrap px-6 py-3 text-slate-600">
                          {METODO_LABEL[p.metodo] ?? p.metodo}
                        </td>
                        <td className="px-6 py-3">
                          {p.tiene_constancia ? (
                            <button
                              type="button"
                              onClick={() => setViendoConstancia(p)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-[color:var(--color-link)] hover:underline"
                            >
                              <FileText className="h-3.5 w-3.5" />
                              Ver
                            </button>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-6 py-3">
                          <Badge variant={ui.variant}>{ui.label}</Badge>
                        </td>
                        <td className="whitespace-nowrap px-6 py-3 text-right font-mono text-slate-800">
                          S/ {Number(p.monto).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {/* El total va al pie de la propia tabla, que es donde se busca
                    en cualquier cuaderno de caja: debajo de la última fila. */}
                <tfoot className="bg-slate-50">
                  <tr>
                    <td colSpan={7} className="px-6 py-3 text-xs text-slate-500">
                      Cobrado{" "}
                      {porMetodo.length > 0 &&
                        `(${porMetodo
                          .map(
                            ([m, total]) =>
                              `${METODO_LABEL[m] ?? m} S/ ${total.toFixed(2)}`,
                          )
                          .join(" · ")})`}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-right font-mono font-semibold text-slate-900">
                      S/ {cobrado.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <ConstanciaViewer
        pagoId={viendoConstancia?.id ?? null}
        titulo={
          viendoConstancia
            ? `${viendoConstancia.paciente_nombre ?? "—"} · ${viendoConstancia.metodo} · S/ ${Number(viendoConstancia.monto).toFixed(2)}`
            : ""
        }
        onClose={() => setViendoConstancia(null)}
      />
    </div>
  );
}

function Th({ children, right = false }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`px-6 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500 ${
        right ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function Resumen({
  icono,
  etiqueta,
  valor,
  nota,
  color,
}: {
  icono: React.ReactNode;
  etiqueta: string;
  valor: string;
  nota?: React.ReactNode;
  color: string;
}) {
  return (
    <Card>
      <CardBody className="flex items-start gap-3">
        <div className={`rounded-lg p-2 ${color}`}>{icono}</div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{etiqueta}</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{valor}</p>
          {nota && <p className="text-xs text-slate-400">{nota}</p>}
        </div>
      </CardBody>
    </Card>
  );
}
